import {
  ESCALATION_KIND_LABELS,
  ESCALATION_STATUS_LABELS,
  type EscalationRow,
  type FieldDefinition,
} from "@estelle/domain";

import { toCsv } from "@/components/table/csv";

/**
 * What the Escalations bulk actions put in the clipboard and in the file
 * (#702). Pure, so the same answer is checked by `scripts/check-escalations.ts`
 * and drawn by the screen.
 *
 * Built from `EscalationRow` and nothing else, so no customer name, phone or
 * address can reach a copied line or the CSV: the row does not hold one.
 */

/** "3 days", "Today": how long it has been open. */
export function daysLabel(days: number): string {
  if (days <= 0) return "Today";
  return `${String(days)} day${days === 1 ? "" : "s"}`;
}

/** The AWBs of the rows, one per line. A row with none is left out. */
export function awbLines(rows: readonly EscalationRow[]): string {
  return rows
    .map((row) => row.awb)
    .filter((awb): awb is string => awb !== null && awb !== "")
    .join("\n");
}

/** The order numbers of the rows, one per line, each once: an order can have two shipments. */
export function orderNumberLines(rows: readonly EscalationRow[]): string {
  return [...new Set(rows.map((row) => row.orderNumber))].join("\n");
}

/** The columns of the CSV, in this order. */
const CSV_FIELDS: FieldDefinition[] = [
  { name: "orderNumber", label: "Order", type: "text" },
  { name: "awb", label: "AWB", type: "text" },
  { name: "courier", label: "Courier", type: "text" },
  { name: "kind", label: "Escalation", type: "text" },
  { name: "level", label: "Level", type: "number" },
  { name: "status", label: "Status", type: "text" },
  { name: "lastScanAt", label: "Last scan", type: "datetime" },
  { name: "attempts", label: "Attempts", type: "number" },
  { name: "daysOpen", label: "Days open", type: "number" },
  { name: "orderValue", label: "Value", type: "money" },
  { name: "payment", label: "COD / prepaid", type: "text" },
];

/** The rows as CSV: raw values a spreadsheet can sort, with the kind and status in words. */
export function escalationCsv(rows: readonly EscalationRow[]): string {
  return toCsv(
    CSV_FIELDS,
    rows.map((row) => ({
      orderNumber: row.orderNumber,
      awb: row.awb ?? "",
      courier: row.courier ?? "",
      kind: ESCALATION_KIND_LABELS[row.kind],
      level: row.level,
      status: ESCALATION_STATUS_LABELS[row.status],
      lastScanAt: row.lastScanAt,
      attempts: row.attempts,
      daysOpen: row.daysOpen,
      orderValue: row.orderValue,
      payment: row.payment === null ? "" : row.payment.toUpperCase(),
    })),
  );
}
